import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import CryptoJS from "crypto-js";

import MetaTags from '../../../components/MetaTags';
import { useAuth } from '../../../context/AuthContext';
import api, { fetchCdDirJogetProfileApi } from '../../../services/api';
import { fetchLookupDataApi } from '../../../services/lookupApi';
import { LOOKUP_MAPPING, CACHE_KEY_AGENCY_LOOKUPS } from '../../../utils/agencyProfileConstants';
import { transformToSelectOptions } from '../../../utils/agencyProfileHelpers';

import TeamMemberHeader from './team/TeamMemberHeader';
import TeamMemberTable, { parseIsReleased } from './team/TeamMemberTable';
import TeamMemberForm from './team/TeamMemberForm';

import './ManageTeamMembers.css';

const SECRET = process.env.REACT_APP_CRYPTO_SECRET || 'SECRET';

const ManageTeamMembersPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [currentJogetRecord, setCurrentJogetRecord] = useState(null);
    const [viewMode, setViewMode] = useState('list'); 
    const [editIndex, setEditIndex] = useState(null);
    const [lookupOptions, setLookupOptions] = useState({ pocDesignation: [], pocContactType: [] });

    const { control, handleSubmit, reset, watch, setValue } = useForm({
        defaultValues: { name: '', email: '', mobile: '', designation: null, contactType: null, permissions: [], enableLogin: false, password: '', isReleased: false }
    });

    const loadData = useCallback(async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            let finalLookups = {};
            const cached = localStorage.getItem(CACHE_KEY_AGENCY_LOOKUPS);
            if (cached) {
                try {
                    const bytes = CryptoJS.AES.decrypt(cached, SECRET);
                    finalLookups = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
                } catch (e) { finalLookups = null; }
            }

            if (!finalLookups || !finalLookups.pocDesignation) {
                const groups = LOOKUP_MAPPING;
                const promises = Object.entries(groups).map(([_, group]) => fetchLookupDataApi(group));
                const results = await Promise.all(promises);
                finalLookups = {};
                Object.keys(groups).forEach((key, i) => finalLookups[key] = transformToSelectOptions(results[i].data.data));
            }
            setLookupOptions(finalLookups);

            const response = await fetchCdDirJogetProfileApi(user.id);
            const jogetData = response?.data?.jogetData;
            if (jogetData) {
                setCurrentJogetRecord(jogetData);
                if (jogetData.teamMembersJson) {
                    const parsed = JSON.parse(jogetData.teamMembersJson);
                    const formatted = parsed.map(m => {
                        const active = m.isActive !== undefined && m.isActive !== null
                            ? (m.isActive === true || m.isActive === 'true' || m.isActive === 1 || m.isActive === '1')
                            : (m.status ? m.status.toString().toLowerCase() === 'active' : !parseIsReleased(m.is_released !== undefined ? m.is_released : m.isReleased));
                        const isRel = !active;
                        return {
                            ...m,
                            designation: finalLookups.pocDesignation?.find(d => d.value === (m.designation?.value || m.designation)) || m.designation,
                            contactType: finalLookups.pocContactType?.find(c => c.value === (m.contactType?.value || m.contactType)) || m.contactType,
                            isActive: active,
                            isReleased: isRel ? 1 : 0,
                            is_released: isRel ? 1 : 0,
                            status: active ? 'Active' : 'Released'
                        };
                    });
                    setTeamMembers(formatted);
                }
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [user]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleShare = async () => {
        const shareTitle = `Team Management - BookMyAudition`;
        const shareText = `Manage team roles and granular access permissions on BookMyAudition.`;
        const shareUrl = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
            } catch (err) { console.log('Share canceled', err); }
        } else {
            try {
                await navigator.clipboard.writeText(shareUrl);
                Swal.fire({
                    icon: 'success',
                    title: 'Link Copied!',
                    text: 'Page link copied to clipboard.',
                    width: '340px',
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600',
                });
            } catch (err) { console.error('Failed to copy', err); }
        }
    };

    const handleOpenAdd = () => {
        setEditIndex(null);
        reset({ 
            name: '', 
            email: '', 
            mobile: '', 
            designation: null, 
            contactType: null, 
            permissions: ['Dashboard:View'], 
            enableLogin: false, 
            password: '', 
            isActive: true, 
            isReleased: 0, 
            is_released: 0 
        });
        setViewMode('form');
    };

    const handleOpenEdit = (index) => {
        setEditIndex(index);
        const member = teamMembers[index];
        const isRel = parseIsReleased(member.is_released !== undefined ? member.is_released : member.isReleased);
        reset({ 
            ...member, 
            password: '', 
            isActive: !isRel,
            isReleased: isRel ? 1 : 0,
            is_released: isRel ? 1 : 0
        });
        setViewMode('form');
    };

    const handleDelete = (index) => {
        Swal.fire({
            title: 'Delete Team Member?',
            text: "This member will be released and removed from your team list.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#E10600',
            cancelButtonColor: '#2b2f3a',
            confirmButtonText: 'Yes, Release & Remove',
            background: '#121212',
            color: '#ffffff'
        }).then((result) => {
            if (result.isConfirmed) {
                const updated = teamMembers.filter((_, i) => i !== index);
                syncBackend(updated);
            }
        });
    };

    const handleFormSubmit = (data) => {
        const inputEmail = data.email?.trim().toLowerCase();
        const inputMobile = data.mobile?.trim();

        const active = data.isActive !== undefined ? Boolean(data.isActive) : !parseIsReleased(data.isReleased);
        const isRel = !active;

        const formattedData = {
            ...data,
            isActive: active,
            isReleased: isRel ? 1 : 0,
            is_released: isRel ? 1 : 0,
            status: active ? 'Active' : 'Released'
        };

        if (editIndex === null) {
            const duplicate = teamMembers.find(
                m => m.email?.trim().toLowerCase() === inputEmail || (inputMobile && m.mobile?.trim() === inputMobile)
            );
            if (duplicate) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Member',
                    text: `A team member with email "${data.email}" or mobile "${data.mobile}" is already in your team list!`,
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600'
                });
                return;
            }
        } else {
            const duplicate = teamMembers.find(
                (m, i) => i !== editIndex && (m.email?.trim().toLowerCase() === inputEmail || (inputMobile && m.mobile?.trim() === inputMobile))
            );
            if (duplicate) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Duplicate Email/Mobile',
                    text: `Another team member already has the email "${data.email}" or mobile "${data.mobile}"!`,
                    background: '#121212',
                    color: '#ffffff',
                    confirmButtonColor: '#E10600'
                });
                return;
            }
        }

        let updatedList = [...teamMembers];
        if (editIndex !== null) updatedList[editIndex] = formattedData;
        else updatedList.push(formattedData);
        
        setViewMode('list');
        syncBackend(updatedList);
    };

    const syncBackend = async (listToSync) => {
        setSaving(true);
        try {
            const payloadToSync = listToSync.map(m => {
                const active = m.isActive !== undefined 
                    ? Boolean(m.isActive) 
                    : !parseIsReleased(m.is_released !== undefined ? m.is_released : m.isReleased);
                const isRel = !active;
                return {
                    ...m,
                    designation: m.designation?.value || m.designation,
                    contactType: m.contactType?.value || m.contactType,
                    isActive: active,
                    isReleased: isRel ? 1 : 0,
                    is_released: isRel ? 1 : 0,
                    status: active ? 'Active' : 'Released'
                };
            });

            console.log("=== FRONTEND syncBackend payloadToSync ===", payloadToSync);

            const payload = {
                ...currentJogetRecord,
                teamMembersJson: JSON.stringify(payloadToSync),
                user_id: user.id
            };
            await api.post('/cddir/profile/cd-dir', payload);
            
            setTeamMembers(listToSync);
            Swal.fire({ icon: 'success', title: 'Updated Successfully', timer: 1500, showConfirmButton: false, background: '#121212', color: '#fff' });
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Sync Error';
            Swal.fire({ 
                icon: 'error', 
                title: 'Operation Failed',
                text: errorMsg,
                confirmButtonColor: '#E10600',
                background: '#121212',
                color: '#ffffff'
            });
            loadData();
        } finally { setSaving(false); }
    };

    if (loading) return (
      <div className="mtm-loading-box">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3 text-muted">Loading Team Members...</p>
      </div>
    );

    return (
        <div className="mtm-page-container">
            <MetaTags title="Team Management" />

            {/* PAGE HEADER */}
            <TeamMemberHeader 
                onShare={handleShare} 
                onOpenAdd={handleOpenAdd} 
                viewMode={viewMode} 
            />

            {/* TABLE LIST VIEW */}
            {viewMode === 'list' && (
                <TeamMemberTable 
                    teamMembers={teamMembers} 
                    onEdit={handleOpenEdit} 
                    onDelete={handleDelete} 
                />
            )}

            {/* FORM VIEW FOR ADD / EDIT MEMBER */}
            {viewMode === 'form' && (
                <TeamMemberForm 
                    control={control}
                    handleSubmit={handleSubmit}
                    setValue={setValue}
                    watch={watch}
                    editIndex={editIndex}
                    lookupOptions={lookupOptions}
                    onCancel={() => setViewMode('list')}
                    onSubmitForm={handleFormSubmit}
                />
            )}

            {saving && <div className="mtm-floating-loader"><Spinner size="sm" className="me-2"/> Synchronizing Data...</div>}
        </div>
    );
};

export default ManageTeamMembersPage;